import { useConfirmation } from '@/contexts/confirmation'
import { EMPLOYEE_CONTRACT_TYPE } from '@/modules/Employee/constants'
import {
    createEmployeeTmp,
    resendConfirmLink,
} from '@/modules/Employee/services'
import { copyToClipboard } from '@/utils/function'
import { paths } from '@/utils/path'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button, Typogaphy } from 'vnetwork-ui'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    CheckCircle2,
    User,
    Mail,
    Hash,
    Briefcase,
    Copy,
    Send,
    ArrowRight,
    FileText,
} from 'lucide-react'

interface EmployeeConfirmationProps {
    employeeTmpData: Awaited<ReturnType<typeof createEmployeeTmp>> | null
}

const EmployeeConfirmation: React.FC<EmployeeConfirmationProps> = ({
    employeeTmpData,
}) => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { setConfirmation } = useConfirmation()

    const { mutateAsync, data, isPending } = useMutationWithHandler(
        {
            mutationFn: resendConfirmLink,
        },
        {
            reactQueryKeys: QUERY_KEYS.EMPLOYEES,
            successMessage: 'resend-success',
            errorMessage: 'error-occurred',
        }
    )

    const handleCopyLink = () => {
        copyToClipboard(
            `${window.location.origin}/util/employee-confirmation/?token=${(data || employeeTmpData)?.result?.token}`,
            t
        )

        navigate(paths.employeesManage)
    }

    return (
        <div className="max-w-2xl mx-auto py-4">
            {/* Success Header */}
            <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                <Typogaphy className="font-semibold text-xl mb-2">
                    {t('account-confirmation')}
                </Typogaphy>
                <Typogaphy className="text-gray-500 text-sm">
                    Tài khoản đã được tạo thành công. Vui lòng gửi link xác nhận
                    cho nhân viên.
                </Typogaphy>
            </div>

            {/* Employee Info Card */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 mb-6">
                <Typogaphy className="font-semibold text-gray-700 mb-4">
                    Thông tin nhân viên
                </Typogaphy>

                <div className="space-y-4">
                    {/* Employee Code */}
                    <div className="flex items-start gap-3">
                        <div className="flex items-center justify-center w-10 h-10 bg-blue-50 rounded-lg flex-shrink-0">
                            <Hash className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1">
                            <Typogaphy className="text-xs text-gray-500 mb-1">
                                {t('employee_code')}
                            </Typogaphy>
                            <Typogaphy className="font-medium text-gray-900">
                                {employeeTmpData?.result?.employee
                                    ?.employee_code || '-'}
                            </Typogaphy>
                        </div>
                    </div>

                    {/* Employee Name */}
                    <div className="flex items-start gap-3">
                        <div className="flex items-center justify-center w-10 h-10 bg-purple-50 rounded-lg flex-shrink-0">
                            <User className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="flex-1">
                            <Typogaphy className="text-xs text-gray-500 mb-1">
                                {t('employee_name')}
                            </Typogaphy>
                            <Typogaphy className="font-medium text-gray-900">
                                {employeeTmpData?.result?.employee
                                    ?.employee_name || '-'}
                            </Typogaphy>
                        </div>
                    </div>

                    {/* Employee Email */}
                    <div className="flex items-start gap-3">
                        <div className="flex items-center justify-center w-10 h-10 bg-green-50 rounded-lg flex-shrink-0">
                            <Mail className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="flex-1">
                            <Typogaphy className="text-xs text-gray-500 mb-1">
                                {t('employee_email_company')}
                            </Typogaphy>
                            <Typogaphy className="font-medium text-gray-900 break-all">
                                {employeeTmpData?.result?.employee
                                    ?.employee_email_company || '-'}
                            </Typogaphy>
                        </div>
                    </div>

                    {/* Employee Type */}
                    <div className="flex items-start gap-3">
                        <div className="flex items-center justify-center w-10 h-10 bg-orange-50 rounded-lg flex-shrink-0">
                            <Briefcase className="w-5 h-5 text-orange-600" />
                        </div>
                        <div className="flex-1">
                            <Typogaphy className="text-xs text-gray-500 mb-1">
                                {t('employee_contract_type')}
                            </Typogaphy>
                            <Typogaphy className="font-medium text-gray-900">
                                {EMPLOYEE_CONTRACT_TYPE(t)?.find(
                                    (item) =>
                                        item.value ===
                                        employeeTmpData?.result?.employee
                                            ?.employee_contract_type
                                )?.label || '-'}
                            </Typogaphy>
                        </div>
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-6">
                <Typogaphy className="font-medium text-gray-700 mb-4">
                    Bước tiếp theo
                </Typogaphy>
                <Typogaphy className="text-sm text-gray-600 mb-4">
                    Gửi link xác nhận cho nhân viên qua email hoặc sao chép link
                    để chia sẻ trực tiếp.
                </Typogaphy>
                <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() =>
                            setConfirmation({
                                title: t('confirm'),
                                description: t('resend-confirmation-warning'),
                                onConfirm: () => {
                                    mutateAsync({
                                        employee_id: Number(
                                            employeeTmpData?.result?.employee
                                                ?.employee_id
                                        ),
                                    })
                                },
                                open: true,
                            })
                        }
                        disabled={isPending}
                        loading={isPending}
                        className="flex-1 sm:flex-initial transition-all duration-200 hover:shadow-lg hover:scale-[1.02] focus:ring-4 focus:ring-blue-300 focus:outline-none"
                        startIcon={<Send className="w-4 h-4" />}
                    >
                        {t('resend-confirmation')}
                    </Button>

                    <Button
                        variant="outlined"
                        disabled={isPending}
                        onClick={handleCopyLink}
                        className="flex-1 sm:flex-initial transition-all duration-200 hover:bg-blue-50 hover:border-blue-500 hover:shadow-md focus:ring-4 focus:ring-blue-200 focus:outline-none"
                        startIcon={<Copy className="w-4 h-4" />}
                    >
                        {t('account-confirmation-copy')}
                    </Button>
                </div>
            </div>

            {/* Email Notification Info */}
            <div className="mt-6 bg-white border border-gray-200 rounded-lg p-5">
                <div className="flex items-start gap-3 mb-4">
                    <div className="flex items-center justify-center w-8 h-8 bg-blue-50 rounded-lg flex-shrink-0">
                        <Mail className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                        <Typogaphy className="font-medium text-gray-900 text-sm mb-1">
                            Email xác nhận sẽ được gửi tới
                        </Typogaphy>
                        <Typogaphy className="text-sm text-gray-600">
                            {employeeTmpData?.result?.employee
                                ?.employee_email_company || '-'}
                        </Typogaphy>
                    </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center gap-2 mb-3">
                        <FileText className="w-4 h-4 text-gray-500" />
                        <Typogaphy className="text-xs font-medium text-gray-700">
                            Nhân viên sẽ điền thông tin như sau:
                        </Typogaphy>
                    </div>

                    {/* Small Demo Preview */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        {/* Mini Header */}
                        <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-200">
                            <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-white text-xs font-bold">
                                    EM
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <Typogaphy className="text-xs font-semibold text-gray-900 truncate">
                                    {employeeTmpData?.result?.employee
                                        ?.employee_name || 'N/A'}
                                </Typogaphy>
                                <Typogaphy className="text-[10px] text-gray-500 truncate">
                                    DEVELOPMENT
                                </Typogaphy>
                            </div>
                        </div>

                        {/* Mini Tabs */}
                        <div className="flex gap-2 mb-3 overflow-x-auto pb-2">
                            <div className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium whitespace-nowrap">
                                Thông Tin Cá Nhân
                            </div>
                            <div className="px-3 py-1.5 bg-white text-gray-500 rounded text-[10px] whitespace-nowrap">
                                Thông Tin Định Danh
                            </div>
                            <div className="px-3 py-1.5 bg-white text-gray-500 rounded text-[10px] whitespace-nowrap">
                                Tài Khoản
                            </div>
                        </div>

                        {/* Mini Form Section */}
                        <div className="bg-blue-50/30 rounded border border-dashed border-blue-200 p-3">
                            <Typogaphy className="text-[10px] font-medium text-gray-700 mb-2">
                                Tải ảnh CCCD
                            </Typogaphy>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="aspect-[3/2] bg-white rounded border border-dashed border-gray-300 flex flex-col items-center justify-center">
                                    <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center mb-1">
                                        <span className="text-gray-400 text-lg">
                                            +
                                        </span>
                                    </div>
                                    <Typogaphy className="text-[9px] text-gray-500">
                                        Ảnh mặt trước
                                    </Typogaphy>
                                </div>
                                <div className="aspect-[3/2] bg-white rounded border border-dashed border-gray-300 flex flex-col items-center justify-center">
                                    <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center mb-1">
                                        <span className="text-gray-400 text-lg">
                                            +
                                        </span>
                                    </div>
                                    <Typogaphy className="text-[9px] text-gray-500">
                                        Ảnh mặt sau
                                    </Typogaphy>
                                </div>
                            </div>
                        </div>

                        {/* Mini Form Fields */}
                        <div className="mt-3 space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                                <div className="bg-white rounded border border-gray-200 px-2 py-1.5">
                                    <Typogaphy className="text-[9px] text-gray-500 mb-0.5">
                                        Email cá nhân
                                    </Typogaphy>
                                    <div className="h-3 bg-gray-100 rounded w-full"></div>
                                </div>
                                <div className="bg-white rounded border border-gray-200 px-2 py-1.5">
                                    <Typogaphy className="text-[9px] text-gray-500 mb-0.5">
                                        Số điện thoại
                                    </Typogaphy>
                                    <div className="h-3 bg-gray-100 rounded w-full"></div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="bg-white rounded border border-gray-200 px-2 py-1.5">
                                    <Typogaphy className="text-[9px] text-gray-500 mb-0.5">
                                        Giới tính
                                    </Typogaphy>
                                    <div className="h-3 bg-gray-100 rounded w-3/4"></div>
                                </div>
                                <div className="bg-white rounded border border-gray-200 px-2 py-1.5">
                                    <Typogaphy className="text-[9px] text-gray-500 mb-0.5">
                                        Ngày sinh
                                    </Typogaphy>
                                    <div className="h-3 bg-gray-100 rounded w-full"></div>
                                </div>
                            </div>
                        </div>

                        {/* Mini Submit Button */}
                        <div className="mt-3 flex justify-end">
                            <div className="bg-blue-600 text-white px-4 py-1.5 rounded text-[10px] font-medium flex items-center gap-1">
                                <span>Tiếp theo</span>
                                <ArrowRight className="w-3 h-3" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default EmployeeConfirmation
